import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams , useNavigate} from "react-router-dom";
import { Button, Modal, Spinner } from "react-bootstrap";
import { format, parseISO } from "date-fns";
import MonthPicker from "../components/DatePicker/MonthPicker";
import { API } from "../data/API/API";
import Datalist, { ListColumn } from "../components/Datalist/Datalist";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faEnvelope, faGear } from "@fortawesome/free-solid-svg-icons";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ReferenceLine,
} from "recharts";

import "./NullReportingSupplierPage.scss";
import NullReportingThresholdModal from "../components/NullReporting/NullReportingThresholdModal";
import { Icons } from "../components/Icons/Icons";

const DEFAULT_YELLOW = 20;
const DEFAULT_RED = 40;
const clsUserLabel = (cls: number | null) => {
  if (cls === 2) return "Röd (kräver uppföljning)";
  if (cls === 1) return "Gul (observera)";
  if (cls === 3) return "Ingen tröskel (ej konfigurerad)";
  if (cls === 0) return "OK";
  return "Ingen nollrapportering att analysera";
};

const periodStatusLabel = (p: any) => {
  const hasInvoices = (p?.invoiceCount ?? 0) > 0 || Number(p?.totalExcMoms ?? 0) !== 0;
  const hasSnapshot = p?.classification !== null && p?.classification !== undefined;

  if (!hasSnapshot && hasInvoices) return "Avvikelse – fakturor rapporterade";
  if (!hasSnapshot && !hasInvoices) return "Ingen data";
  return `Nollrapportering – ${clsUserLabel(p.classification)}`;
};

const clsStyle = (cls: number | null) => {
  // return { fill, stroke }
  if (cls === 2) return { fill: "#ef4444", stroke: "#b91c1c" }; // röd
  if (cls === 1) return { fill: "#fde68a", stroke: "#f59e0b" }; // gul
  if (cls === 3) return { fill: "#e5e7eb", stroke: "#9ca3af" }; // grå (no threshold)
  if (cls === 0) return { fill: "#d1fae5", stroke: "#10b981" }; // grön
  return { fill: "#f3f4f6", stroke: "#d1d5db" };                // ingen snapshot
};

type YearResponse = {
  supplier: { orgNr: string; name?: string | null };
  year: number;
  threshold: null | {
    supplierOrgNr: string;
    limitYellow?: number | null;
    limitRed?: number | null;
    minHistoryMonths: number;
    updated?: string;
    updatedBy?: string | null;
  };
  months: Array<{
    period: string; // yyyy-MM
    snapshot: null | {
      id: number;
      supplierOrgId: string;
      period: string;
      totalAmountPeriod: number;
      historyMonths: number;
      zeroMonths: number;
      zeroRatio: number;
      zeroDeviationValue: number;
      classification: number;
      calcStatus: number;
      calcMessage?: string | null;
      calculatedAt?: string | null;
    };
    invoices: Array<{
      invoiceNumber: string;
      customerOrgId: string;
      customerName?: string | null;
      contractId: string;
      billingDate?: string | null;
      priceExcMoms: number;
      priceIncMoms: number;
    }>;
    invoiceCount: number;
    totalExcMoms: number;
  }>;
};
const YearChartTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;

  const money = moneySEK(Number(p?.totalExcMoms ?? 0));
  const hasInvoices = (p?.invoiceCount ?? 0) > 0 || Number(p?.totalExcMoms ?? 0) !== 0;
  const hasSnapshot = p?.classification !== null && p?.classification !== undefined;

  return (
    <div className="nr-tooltip">
      <div className="nr-tooltip-title">{p.period}</div>

      <div className="nr-tooltip-row">
        <span>Tolkning</span>
        <b>{periodStatusLabel(p)}</b>
      </div>

      <div className="nr-tooltip-row">
        <span>Summa ex moms</span>
        <b>{money}</b>
      </div>

      <div className="nr-tooltip-row">
        <span>Fakturor</span>
        <b>{p.invoiceCount ?? 0}</b>
      </div>

      <div className="nr-tooltip-note">
        {hasSnapshot ? (
          <>
            Historisk nollgrad: <b>{Number(p.zeroRatio ?? 0).toFixed(2)}%</b>{" "}
            <span>({p.zeroMonths}/{p.historyMonths} månader var 0 kr)</span>
          </>
        ) : hasInvoices ? (
          "Den här perioden innehåller fakturor/belopp och bör följas upp."
        ) : (
          "Ingen nollrapportering eller fakturor för perioden."
        )}
      </div>
    </div>
  );
};



function periodToDate(period: string) {
  return parseISO(`${period}-01`);
}
function dateToPeriod(d: Date) {
  return format(d, "yyyy-MM");
}
function moneySEK(v: number) {
  return new Intl.NumberFormat("sv-SE", {
    style: "currency",
    currency: "SEK",
    maximumFractionDigits: 0,
  }).format(v ?? 0);
}

const NullReportingSupplierPage: React.FC = () => {
  const navigate = useNavigate();
  const { orgNr } = useParams<{ orgNr: string }>();
  const location = useLocation();
  const { period = null } = (location.state || {}) as { period?: string | null };

  // ---- period selection
  const initialDate = useMemo(() => {
    if (period) return periodToDate(period);
    // fallback: start with current month
    return new Date();
  }, [period]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
  const selectedPeriod = selectedDate ? dateToPeriod(selectedDate) : null;
  const selectedYear = selectedDate ? selectedDate.getFullYear() : null;

  // ---- caching per year (orgNr+year)
  const cacheRef = useRef<Map<string, YearResponse>>(new Map());
  const cacheKey = useMemo(() => {
    if (!orgNr || !selectedYear) return null;
    return `${orgNr}:${selectedYear}`;
  }, [orgNr, selectedYear]);

  const [loading, setLoading] = useState(true);
  const [yearData, setYearData] = useState<YearResponse | null>(null);

  // ---- threshold modal state
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [pendingYellow, setPendingYellow] = useState<number>(DEFAULT_YELLOW);
  const [pendingRed, setPendingRed] = useState<number>(DEFAULT_RED);
  const [pendingMinHistory, setPendingMinHistory] = useState<number>(12);
  const [savingThreshold, setSavingThreshold] = useState(false);

  const fetchYear = async () => {
    if (!orgNr || !selectedYear || !cacheKey) return;

    // 1) cache hit => instant
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setYearData(cached);
      setLoading(false);
      return;
    }

    // 2) fetch
    setLoading(true);
    try {
      const res = (await API.NullReporting.GetSupplierYear(orgNr, selectedYear)) as YearResponse;
      cacheRef.current.set(cacheKey, res);
      setYearData(res);

      // init threshold editing defaults
      if (res?.threshold) {
        setPendingYellow((res.threshold.limitYellow ?? DEFAULT_YELLOW) as number);
        setPendingRed((res.threshold.limitRed ?? DEFAULT_RED) as number);
        setPendingMinHistory(res.threshold.minHistoryMonths ?? 12);
      } else {
        setPendingYellow(DEFAULT_YELLOW);
        setPendingRed(DEFAULT_RED);
        setPendingMinHistory(12);
      }
    } finally {
      setLoading(false);
    }
  };

  // fetch when orgNr or year changes
  useEffect(() => {
    fetchYear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgNr, selectedYear]);

  // if incoming state.period belongs to a different year than initial fetch, ensure we fetch that year
  useEffect(() => {
    if (!period) return;
    const pYear = periodToDate(period).getFullYear();
    if (selectedDate && selectedDate.getFullYear() !== pYear) {
      setSelectedDate(periodToDate(period));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const supplierTitle = useMemo(() => {
    const name = yearData?.supplier?.name;
    const id = yearData?.supplier?.orgNr ?? orgNr ?? "";
    return name ? `${name} – ${id}` : id;
  }, [yearData, orgNr]);

  const months = yearData?.months ?? [];

  const selectedMonth = useMemo(() => {
    if (!selectedPeriod) return null;
    return months.find((m) => m.period === selectedPeriod) ?? null;
  }, [months, selectedPeriod]);

  const thresholdState = useMemo(() => {
    const t = yearData?.threshold;
    if (!t) return { mode: "gray" as const, yellow: null as number | null, red: null as number | null, minHistoryMonths: 12 };
    return {
      mode: "set" as const,
      yellow: (t.limitYellow ?? null) as number | null,
      red: (t.limitRed ?? null) as number | null,
      minHistoryMonths: t.minHistoryMonths ?? 12,
    };
  }, [yearData]);

  // chart rows: always 12 months in yearData
  const chartData = useMemo(() => {
  return (yearData?.months ?? []).map((m: any) => {
    const hasInvoices = (m.invoiceCount ?? 0) > 0;
    const value = Number(m.totalExcMoms ?? 0);

    // Deviation i grafen: har belopp eller fakturor
    const isDeviation = value !== 0 || hasInvoices;

    return {
      period: m.period,              // yyyy-MM
      month: m.period.slice(5, 7),   // 01..12
      value,
      isDeviation,

      hasSnapshot: !!m.snapshot,
      hasInvoices,
      invoiceCount: m.invoiceCount ?? 0,
      totalExcMoms: value,

      // snapshot fields (kan vara null)
      classification: m.snapshot?.classification ?? null,
      zeroRatio: m.snapshot?.zeroRatio ?? null,
      zeroMonths: m.snapshot?.zeroMonths ?? null,
      historyMonths: m.snapshot?.historyMonths ?? null,
      calcStatus: m.snapshot?.calcStatus ?? null,
      calcMessage: m.snapshot?.calcMessage ?? null,
    };
  });
}, [yearData]);


  const statusLabel = useMemo(() => {
    // Föreslagen status: visar "Avvikelse" om vald månad har non-zero eller invoices; annars OK/Ingen data
    if (!selectedMonth) return "Ingen vald period";
    const hasAny = (selectedMonth.invoiceCount ?? 0) > 0 || (selectedMonth.snapshot != null);
    if (!hasAny) return "Ingen data för perioden";
    if ((selectedMonth.totalExcMoms ?? 0) !== 0 || (selectedMonth.invoiceCount ?? 0) > 0) return "Avvikelse funnen";
    return "OK";
  }, [selectedMonth]);

  const saveThreshold = async () => {
    if (!orgNr || !selectedYear || !cacheKey) return;
    setSavingThreshold(true);
    try {
      await API.NullReporting.UpsertThreshold({
        supplierOrgNr: orgNr,
        limitYellow: pendingYellow,
        limitRed: pendingRed,
        minHistoryMonths: pendingMinHistory,
      });

      // invalidate cache for current year (threshold changed, but months same)
      cacheRef.current.delete(cacheKey);
      setShowThresholdModal(false);
      await fetchYear();
    } finally {
      setSavingThreshold(false);
    }
  };

  // Invoice table
  const invoiceColumns: ListColumn<any>[] = useMemo(
    () => [
      { key: "invoiceNumber", fieldName: "invoiceNumber", name: "Fakturanr", isActive: true, sorting: true, minWidth: 120, maxWidth: "16%" },
      { key: "customerName", fieldName: "customerName", name: "Kund", isActive: true, sorting: true, minWidth: 220, maxWidth: "32%" },
      { key: "customerOrgId", fieldName: "customerOrgId", name: "OrgNr", isActive: true, sorting: true, minWidth: 110, maxWidth: "14%" },
      { key: "contractId", fieldName: "contractId", name: "Avtal", isActive: true, sorting: true, minWidth: 90, maxWidth: "10%" },
      {
        key: "billingDate",
        fieldName: "billingDate",
        name: "Datum",
        isActive: true,
        sorting: true,
        minWidth: 110,
        maxWidth: "12%",
        onRender: (i) => (i.billingDate ? String(i.billingDate).slice(0, 10) : "—"),
      },
      {
        key: "priceExcMoms",
        fieldName: "priceExcMoms",
        name: "Ex moms",
        isActive: true,
        sorting: true,
        minWidth: 120,
        maxWidth: "12%",
        onRender: (i) => moneySEK(Number(i.priceExcMoms ?? 0)),
      },
    ],
    []
  );

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (!yearData || !orgNr || !selectedYear) {
    return <div style={{ padding: 16 }}>Ingen data</div>;
  }

  // selected month snapshot helpers
  const selectedSnapshot = selectedMonth?.snapshot ?? null;
  const selectedInvoices = selectedMonth?.invoices ?? [];
  const selectedTotal = Number(selectedMonth?.totalExcMoms ?? 0);

  return (
  <div className="nullreporting-supplier-page">
    <div className="header">
      <div>
        <h1>{supplierTitle}</h1>
       
      </div>

      
    </div>

    <div className="nr-layout">
      {/* MAIN */}
      <div className="nr-main">
        {/* KPI */}
        <div className="nr-kpis">
          <div className="nr-kpi-card">
            <div className="kpi-icon">
                <Icons.Invoice stroke="white" width={24} height={24}/>
            </div>
            <div className="nr-kpi-title">Fakturor (vald period)</div>
            <div className="nr-kpi-value">{selectedMonth ? `${selectedMonth.invoiceCount ?? 0} st` : "—"}</div>
            <div className="nr-kpi-sub">Antal fakturarader i perioden</div>
          </div>

          <div className="nr-kpi-card">
             <div className="kpi-icon">
                <Icons.Graph  fill="white" width={24} height={24}/>
            </div>
            <div className="nr-kpi-title">Summa ex moms</div>
            <div className="nr-kpi-value">{moneySEK(selectedTotal)}</div>
            <div className="nr-kpi-sub">{selectedPeriod ?? "Ingen vald period"}</div>
          </div>

          <div className="nr-kpi-card">
            <div className="kpi-icon">
                <Icons.Anomaly  fill="white" width={24} height={24}/>
            </div>
            <div className="nr-kpi-title">Nollgrad</div>
            <div className="nr-kpi-value">
              {selectedSnapshot ? `${Number(selectedSnapshot.zeroRatio ?? 0).toFixed(2)}%` : "—"}
            </div>
            <div className="nr-kpi-sub">
              {selectedSnapshot ? `${selectedSnapshot.zeroMonths}/${selectedSnapshot.historyMonths} mån` : "Ingen snapshot"}
            </div>
          </div>
        </div>

        {/* Chart card */}
        <div className="controls" style={{display:'flex',justifyContent:'flex-end'}}>
        <MonthPicker selectedDate={selectedDate} onDateChange={setSelectedDate} loading={false} />
      </div>
        <div className="nr-card">
          <div className="nr-card-head">
            <div>
              <h1 className="nr-card-title">Månadsvis trend ({yearData.year})</h1>
              <div className="nr-card-subtitle">
                Rapporterat belopp (ex moms) per månad. Klicka på en stapel för att välja period.
              </div>
            </div>
            
            <div className="nr-legend">
               
              <div className="nr-legend-item">
                <span className="nr-legend-swatch ok" />
                <span>Noll (OK)</span>
              </div>
              <div className="nr-legend-item">
                <span className="nr-legend-swatch dev" />
                <span>Avvikelse</span>
              </div>
             
            </div>
          </div>

          <div className="nr-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => {
                    const n = Number(v) || 0;
                    if (Math.abs(n) >= 1_000_000) return `${Math.round(n / 1_000_000)}M`;
                    if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000)}k`;
                    return `${n}`;
                  }}
                />
                <Tooltip content={<YearChartTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                <ReferenceLine y={0} stroke="#10b981" strokeWidth={2} />

                <Bar
                  dataKey="value"
                  radius={[6, 6, 0, 0]}
                  barSize={36}
                  onClick={(d: any) => {
                    const period = d?.period;
                    if (period) setSelectedDate(parseISO(`${period}-01`));
                  }}
                >
                  {chartData.map((entry, index) => {
  const s = clsStyle(entry.classification);

  // Om det saknas snapshot men det finns fakturor/belopp: visa röd (avvikelse)
  const effective =
    entry.classification === null && entry.isDeviation
      ? { fill: "#ef4444", stroke: "#b91c1c" }
      : s;

  return (
    <Cell
      key={`cell-${index}`}
      fill={effective.fill}
      stroke={effective.stroke}
      strokeWidth={1}
      opacity={selectedPeriod === entry.period ? 1 : 0.85}
    />
  );
})}

                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Period details / invoices */}
        <div className="nr-card">
          <div className="nr-card-head">
            <div>
              <h1 className="nr-card-title">Perioddetaljer</h1>
              <div className="nr-card-subtitle">
                {selectedPeriod ? `Visar fakturor för ${selectedPeriod}` : "Välj en period för att se detaljer"}
              </div>
            </div>

            {selectedSnapshot && (
              <div className="nr-badges">
  <span className="nr-badge">
    {selectedMonth ? periodStatusLabel({ ...selectedMonth, classification: selectedSnapshot?.classification ?? null }) : "—"}
  </span>
</div>
            )}
          </div>

          {!selectedMonth ? (
            <div className="nr-empty">Ingen vald period</div>
          ) : selectedMonth.snapshot == null && (selectedMonth.invoiceCount ?? 0) === 0 ? (
            <div className="nr-empty">Ingen data för perioden {selectedMonth.period}</div>
          ) : (
            <>
              <div className="nr-period-info">
                <div>
                  <b>Period:</b> {selectedMonth.period}
                </div>
                <div>
                  <b>Fakturor:</b> {selectedMonth.invoiceCount}
                </div>
                <div>
                  <b>Summa ex moms:</b> {moneySEK(selectedTotal)}
                </div>
              </div>

              <div className="nr-table">
                <Datalist
                  items={selectedInvoices}
                  isLoading={false}
                  columns={invoiceColumns}
                  setColumns={() => {}}
                  initialSortKey="billingDate"
                  descendingSort={true}
                  noItemsText="Inga fakturarader för perioden"
                  uniqueKey="invoiceNumber"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* SIDEBAR */}
      <div className="nr-sidebar">
        {/* Status */}
       <div className="nr-sidecard">
  <div className="nr-sidecard-top">
    <div className={`nr-statusbadge ${statusLabel.includes("Avvikelse") ? "dev" : "ok"}`}>
      {statusLabel}
    </div>

    <div className="nr-sidecard-period">
      <div className="nr-sidecard-period-label">Period</div>
      <div className="nr-sidecard-period-value">{selectedPeriod ?? "—"}</div>
    </div>
  </div>

  <div className="nr-sidecard-amount">
    <div className="nr-sidecard-amount-label">Summa ex moms</div>
    <div className="nr-sidecard-amount-value">{moneySEK(selectedTotal)}</div>
  </div>

  <div className="nr-sidecard-actions">
    <Button
      className="nr-sidecard-primary"
      onClick={() => setShowThresholdModal(true)}
    >
      <FontAwesomeIcon icon={faGear} style={{ marginRight: 8 }} />
      Redigera tröskel
    </Button>
          <div className="nr-sidecard-divider">
  <span>Skapa granskning</span>
</div>

    <div className="nr-sidecard-grid2">
      <Button
        className="nr-sidecard-secondary"
        onClick={() => {
          if (!orgNr || !selectedPeriod) return;
          navigate(
            `/nullreporting/reviews/new?supplierOrgNr=${encodeURIComponent(orgNr)}&period=${encodeURIComponent(
              selectedPeriod
            )}&type=email`
          );
        }}
      >
           <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 8 }} />
        Mail
      </Button>

      <Button
        className="nr-sidecard-secondary"
        onClick={() => {
          if (!orgNr || !selectedPeriod) return;
          navigate(
            `/nullreporting/reviews/new?supplierOrgNr=${encodeURIComponent(orgNr)}&period=${encodeURIComponent(
              selectedPeriod
            )}&type=phone`
          );
        }}
      >
         <FontAwesomeIcon icon={faPhone} style={{ marginRight: 8 }} />
        Telefon
      </Button>
    </div>
  </div>
</div>


        {/* Threshold summary */}
        {/* <div className="nr-sidecard">
          <div className="nr-sidecard-title">Tröskel</div>

          {thresholdState.mode === "gray" ? (
            <div className="nr-threshold-gray">Ingen tröskel satt (grå)</div>
          ) : (
            <div className="nr-threshold-values">
              <div className="row">
                <span>Gul</span>
                <b>{thresholdState.yellow ?? DEFAULT_YELLOW}%</b>
              </div>
              <div className="row">
                <span>Röd</span>
                <b>{thresholdState.red ?? DEFAULT_RED}%</b>
              </div>
              <div className="row">
                <span>Min historik</span>
                <b>{thresholdState.minHistoryMonths} mån</b>
              </div>
            </div>
          )}
        </div> */}

        {/* Snapshot info */}
        <div className="nr-sidecard">
          <div className="nr-sidecard-title">Analys för vald period</div>

          {!selectedSnapshot ? (
            <div className="nr-empty-small">Ingen nollrapportering att analysera för vald period.</div>
          ) : (
            <div className="nr-snapshot-grid">
              <div className="item">
                <span>Nollmån</span>
                <b>{selectedSnapshot.zeroMonths}</b>
              </div>
              <div className="item">
                <span>Historik</span>
                <b>{selectedSnapshot.historyMonths}</b>
              </div>
              <div className="item">
                <span>Nollgrad</span>
                <b>{Number(selectedSnapshot.zeroRatio ?? 0).toFixed(2)}%</b>
              </div>
              <div className="item">
                <span>Uppdaterad</span>
                <b>{selectedSnapshot.calculatedAt ? String(selectedSnapshot.calculatedAt).slice(0, 10) : "—"}</b>
              </div>
            </div>
          )}
        </div>

        {/* System info */}
        <div className="nr-infobox">
          <div className="nr-infobox-title">System-info</div>
          <div className="nr-infobox-text">
            Den här vyn är för nollrapportering. Grafen visar årets belopp per månad. Klassificering (OK/Gul/Röd)
            baseras på historisk nollgrad och leverantörens trösklar.
          </div>
        </div>
      </div>
    </div>

    {/* Threshold Modal */}
    <NullReportingThresholdModal
  orgNr={orgNr}
  isOpen={showThresholdModal}
  onClose={() => setShowThresholdModal(false)}
  initialYellow={thresholdState.yellow ?? DEFAULT_YELLOW}
  initialRed={thresholdState.red ?? DEFAULT_RED}
  initialMinHistoryMonths={thresholdState.minHistoryMonths ?? 12}
  onSave={async ({ limitYellow, limitRed, minHistoryMonths }) => {
    // du kan återanvända din saveThreshold-logik men här är "inline" så den är tydlig
    await API.NullReporting.UpsertThreshold({
      supplierOrgNr: orgNr,
      limitYellow,
      limitRed,
      minHistoryMonths,
    });

    // invalidate + refetch (samma som du redan gör)
    if (cacheKey) cacheRef.current.delete(cacheKey);
    await fetchYear();
  }}
/>

  </div>
);

};

export default NullReportingSupplierPage;
